import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import { Role } from '../../shared/interfaces/role.interface';
import { RoleService } from '../../shared/services/permissions-management/role.service';
import { PermissionService } from '../../shared/services/permissions/permission.service';
import { ErrorService } from '../../shared/services/error/error.service';
import { I18nPipe } from '../../shared/i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../shared/i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../shared/i18n/i18n-scoped.service';

const ERROR_SOURCE = 'RolePreviewDialog';

/**
 * Global-admin-only "preview as role" picker (Ctrl+Alt+U). Applies the read-time
 * preview lens via {@link PermissionService.setPreviewRole} — the backend still
 * authorizes the real admin, so this only ever hides UI, never reveals data the
 * real user can't reach. The persistent layout banner (and this dialog's Exit
 * button) end the preview.
 */
@Component({
  selector: 'app-role-preview-dialog',
  templateUrl: './role-preview-dialog.html',
  styleUrls: ['./role-preview-dialog.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressBar,
    I18nPipe,
    I18nScopeReadyDirective,
  ],
  providers: [
    provideI18nScope('ui/role-preview-dialog'),
    I18nScopedService,
  ],
})
export class RolePreviewDialog implements OnInit {
  private readonly roleService = inject(RoleService);
  private readonly permissionService = inject(PermissionService);
  private readonly errorService = inject(ErrorService);
  private readonly dialogRef = inject(MatDialogRef<RolePreviewDialog>);

  isLoading = signal(true);
  roles = signal<Role[]>([]);
  searchQuery = signal('');

  readonly activePreview = this.permissionService.previewRole;

  readonly filteredRoles = computed(() => {
    const all = this.roles();
    const query = this.searchQuery().toLocaleLowerCase().trim();
    if (!query) return all;
    return all.filter(
      (r) =>
        r.name.toLocaleLowerCase().includes(query) ||
        r.description?.toLocaleLowerCase().includes(query),
    );
  });

  ngOnInit(): void {
    this.loadRoles();
  }

  private async loadRoles(): Promise<void> {
    this.isLoading.set(true);
    try {
      const fetched = await firstValueFrom(this.roleService.getAllRoles());
      this.roles.set(fetched ?? []);
    } catch (err) {
      this.errorService.report(err, {
        source: ERROR_SOURCE,
        action: 'loadRoles',
      });
    } finally {
      this.isLoading.set(false);
    }
  }

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
  }

  isActive(role: Role): boolean {
    return this.activePreview()?.id === role._id;
  }

  selectRole(role: Role): void {
    this.permissionService.setPreviewRole({
      id: role._id,
      name: role.name,
      permissions: new Set(role.permissions),
      sectionAccess: role.sectionAccess ?? {},
    });
    this.dialogRef.close();
  }

  exitPreview(): void {
    this.permissionService.clearPreviewRole();
    this.dialogRef.close();
  }

  close(): void {
    this.dialogRef.close();
  }
}
